/*
 * The SDL2/Emscripten combination is greedy when it comes to consuming keyboard events.
 * By default, it will prevent the DOM from receiving any keyboard events in the normal
 * fashion, no matter what DOM element is in focus. This will not work well for us.
 *
 * There are a few potential workarounds. As a preference I would keep this entirely within
 * the C++ side, but unfortunately none of those options worked out.
 *
 * - If we were using SDL3, then SDL_HINT_EMSCRIPTEN_KEYBOARD_ELEMENT could be very interesting.
 *
 * - emscripten_set_focus_callback has potential, but would require using a DOM selector for the
 * canvas elements, and we don't want to enforce an `id` etc set on the canvas.
 *
 * - I also tried listening for SDL_WINDOWEVENT_FOCUS_GAINED/SDL_WINDOWEVENT_FOCUS_LOST events in
 * RenderSurfaceHolderImplLinuxSDL::handleWindowEvent, but SDL_WINDOWEVENT_FOCUS_LOST never seemed
 * to be fired.
 *
 * Therefore, for now we adopt a JS based approach. Maybe there would be some options to do this
 * via Emscripten JS within the C++ files, e.g. EM_ASM - but again accessing the HTMLCanvasElement
 * could be tricky, and too much EM_ASM doesn't seem that clean. So - for now - the package owns
 * this. Perhaps with an upgrade to SDL3 we could remove all this.
 */
export const setupInteractivity = (canvas, application) => {
    // Prevent the default "show context menu on right-click or ctrl + click" behaviour,
    // as we will want to handle these type of events with SDL.
    canvas.addEventListener("contextmenu", (e) => {
        e.preventDefault();
    });
    // Canvas elements require `tabindex="0"` to be focusable.
    canvas.setAttribute("tabIndex", "0");
    // Q) Why not const `onFocus = application.enableKeyboardEvents`?
    // A) Wasm does not like to receive arguments for functions which do not expect arguments.
    //    However, TypeScript allows `() => void` to be attached as a listener to `(evt: Foo) => void`
    const onFocus = () => application.enableKeyboardEvents();
    const onBlur = () => application.disableKeyboardEvents();
    // Sync keyboard event state to initial focus state of the canvas.
    if (document.activeElement === canvas) {
        onFocus();
    }
    else {
        onBlur();
    }
    canvas.addEventListener("focus", onFocus);
    canvas.addEventListener("blur", onBlur);
    return {
        cleanupInteractivity: () => {
            canvas.removeEventListener("focus", onFocus);
            canvas.removeEventListener("blur", onBlur);
        },
    };
};
